import { Component, computed, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { FilledButtonComponent } from '@custom-components/general/button';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import {
  FilterGroup,
  TableFilterBarComponent,
  TypeaheadItem,
} from '@custom-components/general/table-filter-bar/table-filter-bar.component';
import { INITIAL_CONFIG_FILES_TABLE_COLUMNS_CONFIG, PAGINATION_DEFAULT } from '@core/constants';
import { InitialConfigFileModel, InitialConfigFileFilterModel } from '@core/models/setting.model';
import { InitialConfigFileDetailsDialogComponent } from './initial-config-file-details-dialog/initial-config-file-details-dialog.component';
import { ConfigFileUploadDialogComponent } from '../../components/config-file-upload-dialog/config-file-upload-dialog.component';
import { SettingsStore } from '@root/app/store/settings.store';
import { CompanyStore } from '@root/app/store/company.store';
import { DeviceStore } from '@root/app/store/device.store';
import { CompanyService } from '@core/services/company.service';

@Component({
  selector: 'xemex-initial-config-files',
  imports: [FilledButtonComponent, TableFilterBarComponent, TranslateModule, TableComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './initial-config-files.component.html',
})
export class InitialConfigFilesComponent {
  settingsStore = inject(SettingsStore);
  companyStore = inject(CompanyStore);
  deviceStore = inject(DeviceStore);
  private readonly companyService = inject(CompanyService);
  readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  readonly columns = INITIAL_CONFIG_FILES_TABLE_COLUMNS_CONFIG;

  filter: InitialConfigFileFilterModel = {
    includeDeleted: false,
    partnerOid: undefined,
    deviceTypeId: undefined,
  } as InitialConfigFileFilterModel;

  // Async data source handed to the filter-bar partner typeahead pill.
  protected readonly partnerSearch = (query: string): Promise<TypeaheadItem[]> =>
    firstValueFrom(
      this.companyService.getListCompaniesFilter({ limit: 10, offset: 0, partnerName: query }),
    ).then(res => (res.partners ?? []).map(p => ({ id: p.partnerOid, label: p.partnerName })));

  protected readonly isLoading = computed(() => this.settingsStore.isLoadingInitialConfigFiles());
  protected readonly totalCount = computed(
    () => this.settingsStore.initialConfigFilesData()?.numberOfItems ?? 0,
  );

  protected readonly tableData = computed(() => {
    const files = this.settingsStore.initialConfigFilesData()?.files ?? [];
    const cache = this.companyStore.partnerNameCache();
    return files.map((f: InitialConfigFileModel) => ({
      ...f,
      partnerNames: (f.partnerOids ?? [])
        .map((oid: string) => cache[oid])
        .filter(Boolean)
        .join(', '),
    })) as unknown as Record<string, unknown>[];
  });

  constructor() {
    if (!this.deviceStore.deviceTypes()) {
      this.deviceStore.getDeviceTypesMethod();
    }

    if (!this.settingsStore.initialConfigFilesData()) {
      this.settingsStore.getInitialConfigFiles({
        offset: PAGINATION_DEFAULT.offset,
        limit: PAGINATION_DEFAULT.limit,
        includeDeleted: false,
      });
    }

    toObservable(this.settingsStore.initialConfigFilesData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        const uniqueOids = [
          ...new Set(data?.files.flatMap((f: InitialConfigFileModel) => f.partnerOids ?? []) ?? []),
        ];
        uniqueOids.forEach(oid => this.companyStore.getPartnerByIdMethod(oid));
      });
  }

  protected readonly filterGroups = computed<FilterGroup[]>(() => {
    const groups: FilterGroup[] = [];
    const types = this.deviceTypes;
    if (types.length) {
      groups.push({
        key: 'deviceType',
        label: this.translate.instant('device.deviceType'),
        options: types.map((t: { description: string }) => t.description),
        selection: 'single',
      });
    }
    groups.push({
      key: 'partner',
      label: this.translate.instant('search.companies.title'),
      type: 'typeahead',
      selection: 'single',
      search: this.partnerSearch,
      placeholder: this.translate.instant('general.placeholderInput'),
    });
    groups.push({
      key: 'showDeleted',
      label: this.translate.instant('settings.configFiles.filter.showDeletedFiles'),
      type: 'toggle',
    });
    return groups;
  });

  protected onPillFilterChange(filters: Record<string, string[]>): void {
    const typeName = filters['deviceType']?.[0];
    const partnerOid = filters['partner']?.[0];
    this.filter.deviceTypeId = typeName
      ? this.deviceTypes.find((t: { description: string }) => t.description === typeName)
          ?.deviceTypeId
      : undefined;
    this.filter.includeDeleted = filters['showDeleted']?.includes('true') ?? false;
    this.filter.partnerOid = partnerOid || undefined;
    this.onApplyFilter();
  }

  openUploadDialog(): void {
    this.dialog.open(ConfigFileUploadDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      height: '80vh',
      data: { isDeviceConfigFile: false },
    });
  }

  protected onRowClick(row: Record<string, unknown>): void {
    this.dialog.open(InitialConfigFileDetailsDialogComponent, {
      width: '850px',
      maxWidth: '850px',
      maxHeight: '90vh',
      panelClass: 'h-full',
      data: { fileId: row['fileId'] },
    });
  }

  protected onPageChange(evt: PageEvent): void {
    this.settingsStore.getInitialConfigFiles({
      offset: evt.pageIndex,
      limit: PAGINATION_DEFAULT.limit,
      includeDeleted: this.filter.includeDeleted,
    });
  }

  onApplyFilter(): void {
    this.settingsStore.getInitialConfigFiles({ ...this.filter });
  }

  get deviceTypes() {
    return (this.deviceStore.deviceTypes()?.['device-types'] ?? []).filter(
      (d: { supportsTopology: boolean; protocol: string }) =>
        d.supportsTopology && d.protocol === 'DLMS',
    );
  }
}
